import { Haptics, ImpactStyle, NotificationType } from '@capacitor/haptics'

/**
 * Tactile feedback — a synthesized "tick" (Web Audio, no assets, works in
 * WKWebView + every browser) paired with a haptic pulse. Haptics use the
 * Capacitor plugin: real Taptic Engine on the native iOS/Android shell,
 * navigator.vibrate via the plugin's web impl elsewhere, a silent no-op if
 * the native plugin isn't synced yet. Everything is gated on one pref
 * (Profile → Sound & haptics) and every call is failure-swallowed so
 * feedback can never break an interaction.
 *
 * Sound is deliberately quiet + short (a UI tick, not a chime). Fires only
 * on real gestures (click/tap), which also satisfies the browser autoplay
 * gesture requirement for resuming the AudioContext.
 *
 * ── SHARED, NOT MOBILE-ONLY (moved from web/src/mobile/lib/, 2026-08-11) ──
 * This lived under `web/src/mobile/` and nothing outside that directory
 * imported it, so the DESKTOP shell had zero press feedback — and seats /
 * org billing are web-only (docs/ORG-ACCOUNTS.md), i.e. the one screen where
 * money changes hands was the least tactile surface in the product. It now
 * lives in `web/src/lib/` and both shells share it;
 * `web/src/mobile/lib/feedback.ts` is a re-export shim so existing mobile
 * imports keep working.
 *
 * ── THE VOCABULARY HAS A NEGATIVE HALF ────────────────────────────────────
 * `tap` / `select` / `toggle` / `success` are monotonically positive. There
 * was no verb for loss, dismissal or refusal, so signing a device out,
 * hitting a seat limit and a rejected KYC all landed on the same happy tick.
 * `dim()` = something receded (a device signed out, a row removed).
 * `deny()` = the product refused (seat limit, 403, rejected).
 * Both take an OPTIONAL element and paint a visual on it, because on desktop
 * web there is no haptic hardware at all: `navigator.vibrate` is a no-op on
 * every desktop browser, so a purely haptic "no" is silence. The visual is
 * deliberately NOT gated on the sound pref — a refusal must stay visible for
 * someone who turned sound off — and it collapses under
 * `prefers-reduced-motion` via its own CSS block (`index.css`, "negative
 * feedback" section).
 */

const PREF_KEY = 'aska:feedback'

function readPref(): boolean {
  try {
    return localStorage.getItem(PREF_KEY) !== 'off'
  } catch {
    return true
  }
}

let enabled = readPref()

export function feedbackEnabled(): boolean {
  return enabled
}

export function setFeedbackEnabled(on: boolean): void {
  enabled = on
  try {
    localStorage.setItem(PREF_KEY, on ? 'on' : 'off')
  } catch {
    /* private mode — keep the in-memory value */
  }
  if (on) feedback.select() // audible confirmation when turning it on
}

// ── Web Audio "tick" ────────────────────────────────────────────────
let ctx: AudioContext | null = null

function audio(): AudioContext | null {
  if (typeof window === 'undefined') return null
  try {
    if (!ctx) {
      const AC =
        window.AudioContext ||
        (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
      if (!AC) return null
      ctx = new AC()
    }
    if (ctx.state === 'suspended') void ctx.resume()
    return ctx
  } catch {
    return null
  }
}

function tick(freq: number, dur: number, gain: number): void {
  const c = audio()
  if (!c) return
  try {
    const osc = c.createOscillator()
    const amp = c.createGain()
    osc.type = 'triangle'
    osc.frequency.value = freq
    const t = c.currentTime
    amp.gain.setValueAtTime(0.0001, t)
    amp.gain.exponentialRampToValueAtTime(gain, t + 0.004)
    amp.gain.exponentialRampToValueAtTime(0.0001, t + dur)
    osc.connect(amp).connect(c.destination)
    osc.start(t)
    osc.stop(t + dur + 0.02)
  } catch {
    /* audio unavailable */
  }
}

function buzz(style: ImpactStyle): void {
  // Native shell → Taptic Engine; web → navigator.vibrate via the plugin;
  // not-yet-synced native → rejects, swallowed.
  void Haptics.impact({ style }).catch(() => {})
}

function notify(type: NotificationType): void {
  // iOS/Android notification haptics are a genuinely different texture from an
  // impact — the right primitive for "warning"/"error". Same swallow.
  void Haptics.notification({ type }).catch(() => {})
}

// ── Visual half of the negative verbs ───────────────────────────────
//
// On desktop web there is no vibration motor, so `dim`/`deny` would be
// inaudible-and-invisible for anyone with sound off. These paint the class,
// then strip it so a repeat press replays the animation. `animationend` is
// the primary removal; the timeout is the fallback for the reduced-motion
// case, where the CSS sets `animation: none` and no event ever fires.

const VISUAL_MS = 420

function flash(el: Element | null | undefined, cls: string): void {
  if (!el || typeof (el as HTMLElement).classList?.add !== 'function') return
  const node = el as HTMLElement
  try {
    node.classList.remove(cls)
    // Force a reflow so re-adding within the same frame restarts the animation.
    void node.offsetWidth
    node.classList.add(cls)
    const done = () => {
      node.classList.remove(cls)
      node.removeEventListener('animationend', done)
    }
    node.addEventListener('animationend', done)
    window.setTimeout(done, VISUAL_MS)
  } catch {
    /* detached node / exotic element — feedback never breaks an interaction */
  }
}

/** Paint the refusal nudge on an element without any sound or haptic. */
export function denyVisual(el: Element | null | undefined): void {
  flash(el, 'fb-deny')
}

/** Paint the recede dip on an element without any sound or haptic. */
export function dimVisual(el: Element | null | undefined): void {
  flash(el, 'fb-dim')
}

export const feedback = {
  /** Light tap — the default button/press. */
  tap(): void {
    if (!enabled) return
    tick(1900, 0.028, 0.045)
    buzz(ImpactStyle.Light)
  },
  /** Selection change — tab switch, segmented control, chip. */
  select(): void {
    if (!enabled) return
    tick(2300, 0.03, 0.05)
    buzz(ImpactStyle.Light)
  },
  /** Toggle on/off — distinct up (on) / down (off) pitch. */
  toggle(on: boolean): void {
    if (!enabled) return
    tick(on ? 2600 : 1500, 0.04, 0.055)
    buzz(ImpactStyle.Medium)
  },
  /** Positive completion — add to cart, message sent. A two-note rise. */
  success(): void {
    if (!enabled) return
    tick(2000, 0.03, 0.05)
    window.setTimeout(() => tick(2900, 0.05, 0.05), 45)
    buzz(ImpactStyle.Medium)
  },
  /**
   * Something receded — a device signed out, a member removed, a card
   * dismissed, a value lost. ONE low note falling away: deliberately below
   * `tap` in pitch and quieter, so a loss can never be mistaken for a
   * confirmation. Pass the element that is going away to dip it visually.
   */
  dim(el?: Element | null): void {
    dimVisual(el) // not pref-gated: the visual IS the feedback on desktop
    if (!enabled) return
    tick(1100, 0.05, 0.038)
    buzz(ImpactStyle.Light)
  },
  /**
   * Refusal — the seat limit, a 403 on a forwarded invite, a rejected
   * document, a disabled control someone keeps pressing. A low, unmusical
   * double thud that shares nothing with `success()`'s rise. Pass the control
   * that refused to nudge it, which is what makes this legible on desktop.
   */
  deny(el?: Element | null): void {
    denyVisual(el) // not pref-gated: a refusal must stay visible with sound off
    if (!enabled) return
    tick(520, 0.06, 0.06)
    window.setTimeout(() => tick(400, 0.075, 0.055), 80)
    notify(NotificationType.Error)
  },
}
